<template>
  <ThemedApp>
    <!-- Preloader component -->
    <AppPreloader />
    <!-- Router view renders layout based on route (ShopperLayout or AdminLayout) -->
    <router-view />
  </ThemedApp>
</template>

<script setup lang="ts">
/**
 * App.vue - Minimal root component
 *
 * This component only provides:
 * - ThemedApp wrapper for theme management
 * - AppPreloader for loading states
 * - Single router-view that renders the appropriate layout
 *
 * All layout-specific logic has been moved to:
 * - ShopperLayout.vue - customer-facing pages
 * - AdminLayout.vue - store admin pages
 */

import ThemedApp from '@/components/ThemedApp.vue'
import AppPreloader from '@/components/layout/Preloader.vue'
</script>

<style>
/* Global App Styles */
* {
  box-sizing: border-box;
}

/* Smooth Scrolling */
html {
  scroll-behavior: smooth;
}

/* Responsive Images */
img {
  max-width: 100%;
  height: auto;
  display: block;
}

/* Vuetify Transition Enhancement */
.v-fade-transition-enter-active,
.v-fade-transition-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-fade-transition-enter-from,
.v-fade-transition-leave-to {
  opacity: 0;
}

/* Global Container Improvements */
.v-container {
  max-width: 1400px;
}

/* Keep desktop content snug against the drawer (routes also wrap in v-main) */
.v-main > .v-main {
  --v-layout-left: 0px !important;
  --v-layout-right: 0px !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

@media (min-width: 1280px) {
  .v-main .v-container:not(.v-container--fluid) {
    max-width: calc(100% - 48px);
    margin-left: 0;
    margin-right: 0;
  }
}

/* Global Responsive Styles */
@media (max-width: 960px) {
  .v-container {
    padding: 16px !important;
  }
}

@media (max-width: 600px) {
  .v-container {
    padding: 12px !important;
  }

  .v-col {
    padding: 6px !important;
  }

  .main-content {
    min-height: calc(100vh - 60px);
  }

  /* Typography Adjustments */
  .text-h1 {
    font-size: 2.25rem !important;
    line-height: 1.2 !important;
  }

  .text-h2 {
    font-size: 1.75rem !important;
    line-height: 1.2 !important;
  }

  .text-h3 {
    font-size: 1.5rem !important;
    line-height: 1.3 !important;
  }

  .text-h4 {
    font-size: 1.25rem !important;
    line-height: 1.3 !important;
  }

  /* Button Adjustments */
  .v-btn.v-btn--size-large {
    padding: 0 16px !important;
    height: 42px !important;
    font-size: 0.9rem !important;
  }
}

/* Accessibility Improvements */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }

  .v-progress-circular--indeterminate > svg,
  .v-progress-circular--indeterminate .v-progress-circular__overlay {
    animation-duration: 1.4s !important;
    animation-iteration-count: infinite !important;
  }
}

/* Focus Styles for Accessibility */
:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(var(--v-theme-surface-rgb), 0.5);
}

::-webkit-scrollbar-thumb {
  background: rgba(var(--v-theme-primary-rgb), 0.5);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--v-theme-primary-rgb), 0.7);
}
</style>
